<template>
  <div class="pdf-page-remover">
    <h1 class="text-3xl font-bold">PDF Page Remover</h1>
    <p class="text-gray-600 mt-2 mb-4">Upload a PDF and remove specific pages</p>

    <div class="upload-section mb-6">
      <label
        class="block w-full max-w-md mx-auto p-6 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-green-500 hover:bg-slate-800/50 transition-colors bg-slate-800"
        :class="{ 'border-green-500 bg-slate-800': pdfInfo }"
      >
        <input
          type="file"
          ref="fileInput"
          @change="onFileChange"
          accept=".pdf,application/pdf"
          class="hidden"
        />
        <div class="text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
            <path d="M28 8H12a4 4 0 00-4 4v24a4 4 0 004 4h24a4 4 0 004-4V20L28 8z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <polyline points="28,8 28,20 40,20" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <p class="mt-2 text-sm text-slate-400">
            {{ pdfInfo ? pdfInfo.fileName : 'Click to upload PDF' }}
          </p>
        </div>
      </label>
    </div>

    <div v-if="isLoading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-green-500 border-t-transparent"></div>
      <p class="text-gray-600 mt-2">Loading PDF...</p>
    </div>

    <div v-if="error && !isLoading" class="max-w-md mx-auto bg-red-50 text-red-600 rounded-lg p-4 mb-4">
      {{ error }}
    </div>

    <div v-if="pdfInfo && !isLoading" class="pdf-controls max-w-md mx-auto">
      <div class="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-4">
        <p class="text-lg font-semibold">
          Total pages: <span class="text-green-600">{{ pdfInfo.pageCount }}</span>
        </p>
      </div>

      <div class="page-selection mb-4">
        <label class="block font-medium mb-2 text-left">Pages to remove:</label>
        <input
          type="text"
          v-model="pagesToRemove"
          placeholder="e.g., 1, 3, 5-7, 10, 15"
          class="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-lg text-slate-200 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none placeholder-slate-500"
        />
        <p class="text-sm text-slate-500 mt-2 text-left">
          Enter page numbers separated by commas. Use ranges like 5-7 for consecutive pages.
          Pages can be in any order.
        </p>
      </div>

      <div v-if="parsedPages.length > 0" class="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-4 text-left">
        <p class="text-sm mb-1">
          <span class="font-medium">Pages to remove:</span>
          <span class="text-red-600 font-mono">{{ parsedPages.join(', ') }}</span>
        </p>
        <p class="text-sm">
          <span class="font-medium">Remaining pages:</span>
          <span class="text-green-600">{{ pdfInfo.pageCount - parsedPages.length }}</span>
        </p>
      </div>

      <div v-if="successMessage" class="bg-green-50 text-green-600 rounded-lg p-4 mb-4">
        {{ successMessage }}
      </div>

      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          class="action-btn bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          @click="processRemovePages"
          :disabled="parsedPages.length === 0 || isProcessing"
        >
          {{ isProcessing ? 'Processing...' : 'Remove Pages' }}
        </button>

        <button
          v-if="downloadUrl"
          class="action-btn bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          @click="downloadPdf"
        >
          Download Modified PDF
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onUnmounted } from 'vue';
import { PDFDocument } from 'pdf-lib';

interface PdfInfo {
  pageCount: number;
  fileName: string;
}

export default defineComponent({
  name: 'PDFPageRemover',
  setup() {
    const fileInput = ref<HTMLInputElement | null>(null);
    const pdfInfo = ref<PdfInfo | null>(null);
    const pagesToRemove = ref('');
    const error = ref('');
    const successMessage = ref('');
    const isLoading = ref(false);
    const isProcessing = ref(false);
    const downloadUrl = ref<string | null>(null);
    const downloadFileName = ref('');

    // Store raw bytes outside of Vue reactivity
    let originalPdfBytes: Uint8Array | null = null;

    // Clean up blob URL on unmount
    onUnmounted(() => {
      if (downloadUrl.value) {
        URL.revokeObjectURL(downloadUrl.value);
      }
    });

    const parsedPages = computed(() => {
      if (!pagesToRemove.value.trim() || !pdfInfo.value) return [];

      const pages: number[] = [];
      const parts = pagesToRemove.value.split(',');

      for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed) continue;

        if (trimmed.includes('-')) {
          const rangeParts = trimmed.split('-');
          if (rangeParts.length === 2) {
            const start = parseInt(rangeParts[0].trim(), 10);
            const end = parseInt(rangeParts[1].trim(), 10);
            if (!isNaN(start) && !isNaN(end) && start <= end) {
              for (let i = start; i <= end; i++) {
                if (i >= 1 && i <= pdfInfo.value!.pageCount && !pages.includes(i)) {
                  pages.push(i);
                }
              }
            }
          }
        } else {
          const num = parseInt(trimmed, 10);
          if (!isNaN(num) && num >= 1 && num <= pdfInfo.value!.pageCount && !pages.includes(num)) {
            pages.push(num);
          }
        }
      }

      return pages.sort((a, b) => a - b);
    });

    async function onFileChange(e: Event) {
      const target = e.target as HTMLInputElement;
      if (!target.files || !target.files[0]) {
        return;
      }

      const file = target.files[0];

      // Reset state
      isLoading.value = true;
      error.value = '';
      successMessage.value = '';
      pagesToRemove.value = '';
      pdfInfo.value = null;
      originalPdfBytes = null;

      // Clean up previous download URL
      if (downloadUrl.value) {
        URL.revokeObjectURL(downloadUrl.value);
        downloadUrl.value = null;
      }

      try {
        // Read file as Uint8Array
        const arrayBuffer = await file.arrayBuffer();
        const pdfBytes = new Uint8Array(arrayBuffer);

        // Check if PDF is encrypted by looking for encryption markers in the raw bytes
        const pdfText = new TextDecoder('latin1').decode(pdfBytes.slice(0, Math.min(pdfBytes.length, 10000)));
        if (pdfText.includes('/Encrypt')) {
          error.value = 'This PDF is encrypted or password-protected and cannot be processed. Please use an unencrypted PDF.';
          pdfInfo.value = null;
          originalPdfBytes = null;
          isLoading.value = false;
          return;
        }

        // Try to load the PDF
        const pdfDoc = await PDFDocument.load(pdfBytes);

        const pageCount = pdfDoc.getPageCount();

        if (pageCount === 0) {
          error.value = 'This PDF has no pages.';
          pdfInfo.value = null;
          originalPdfBytes = null;
          isLoading.value = false;
          return;
        }

        // Store the bytes only after successful load
        originalPdfBytes = pdfBytes;

        pdfInfo.value = {
          pageCount: pageCount,
          fileName: file.name
        };

      } catch (err: unknown) {
        console.error('PDF load error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';

        if (errorMessage.toLowerCase().includes('encrypt') || errorMessage.toLowerCase().includes('password')) {
          error.value = 'This PDF is encrypted or password-protected and cannot be processed. Please use an unencrypted PDF.';
        } else {
          error.value = `Failed to load PDF: ${errorMessage}`;
        }
        pdfInfo.value = null;
        originalPdfBytes = null;
      } finally {
        isLoading.value = false;
      }
    }

    async function processRemovePages() {
      if (!originalPdfBytes || !pdfInfo.value || parsedPages.value.length === 0) {
        error.value = 'Please upload a PDF and specify pages to remove.';
        return;
      }

      if (parsedPages.value.length >= pdfInfo.value.pageCount) {
        error.value = 'Cannot remove all pages from the PDF. At least one page must remain.';
        return;
      }

      isProcessing.value = true;
      error.value = '';
      successMessage.value = '';

      // Clean up previous download URL
      if (downloadUrl.value) {
        URL.revokeObjectURL(downloadUrl.value);
        downloadUrl.value = null;
      }

      try {
        // Load the PDF document - make a copy of the bytes to avoid mutation issues
        const pdfDocBytes = new Uint8Array(originalPdfBytes);
        const pdfDoc = await PDFDocument.load(pdfDocBytes);

        // Remove pages in reverse order to maintain correct indices
        const sortedDesc = [...parsedPages.value].sort((a, b) => b - a);
        for (const pageNum of sortedDesc) {
          pdfDoc.removePage(pageNum - 1); // Convert to 0-based index
        }

        // Save the modified document
        const pdfBytes = await pdfDoc.save();

        // Create blob URL for download
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        downloadUrl.value = URL.createObjectURL(blob);

        const originalName = pdfInfo.value.fileName.replace(/\.pdf$/i, '');
        downloadFileName.value = `${originalName}-modified.pdf`;

        successMessage.value = `Successfully removed ${parsedPages.value.length} page(s). Click "Download Modified PDF" to save.`;
      } catch (err: unknown) {
        console.error('PDF processing error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        error.value = `Failed to process PDF: ${errorMessage}`;
      } finally {
        isProcessing.value = false;
      }
    }

    function downloadPdf() {
      if (!downloadUrl.value) return;

      const link = document.createElement('a');
      link.href = downloadUrl.value;
      link.download = downloadFileName.value;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    return {
      fileInput,
      pdfInfo,
      pagesToRemove,
      parsedPages,
      error,
      successMessage,
      isLoading,
      isProcessing,
      downloadUrl,
      onFileChange,
      processRemovePages,
      downloadPdf
    };
  }
});
</script>

<style scoped>
.pdf-page-remover {
  text-align: center;
  margin-top: 20px;
  padding: 0 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
